import { create } from 'zustand'
import {
	fetchMaterials,
	fetchMaterialsAdmin,
	fetchMaterialById,
	fetchMaterialsByRelayType,
	fetchCategoryStats,
	fetchRelayTypeStats,
	logMaterialView,
	pinMaterial,
	unpinMaterial,
	fetchUserHistory,
	fetchUserPins,
	createMaterialApi,
	updateMaterialApi,
	deleteMaterialApi,
	publishMaterialApi,
	unpublishMaterialApi,
	addResourceApi,
	deleteResourceApi
} from '../api/learning'
import {
	CategoryStat,
	CreateMaterialPayload,
	CreateResourcePayload,
	LearningCategory,
	LearningMaterial,
	RelayTypeStat,
	UpdateMaterialPayload,
	ViewLogResponse
} from '../helpers/types/learning'

export interface LearningState {
	materials: LearningMaterial[]
	total: number
	selectedMaterial: LearningMaterial | null
	categoryStats: CategoryStat[]
	relayTypeStats: RelayTypeStat[]
	userHistory: LearningMaterial[]
	userPins: LearningMaterial[]
	relatedMaterials: LearningMaterial[]
	loading: boolean

	fetchMaterials: (params?: {
		page?: number
		limit?: number
		category?: LearningCategory
		relayTypeId?: string
		search?: string
	}) => Promise<void>
	fetchMaterialsAdmin: (params?: {
		page?: number
		limit?: number
		category?: LearningCategory
		relayTypeId?: string
		search?: string
	}) => Promise<void>
	fetchMaterialById: (id: string) => Promise<LearningMaterial>
	fetchMaterialsByRelayType: (
		relayTypeId: string,
		page?: number,
		limit?: number
	) => Promise<void>
	fetchCategoryStats: () => Promise<void>
	fetchRelayTypeStats: () => Promise<void>

	logView: (materialId: string) => Promise<ViewLogResponse>
	togglePin: (
		materialId: string,
		isPinned: boolean
	) => Promise<void>
	fetchUserHistory: (limit?: number) => Promise<void>
	fetchUserPins: () => Promise<void>

	createMaterial: (
		payload: CreateMaterialPayload
	) => Promise<LearningMaterial>
	updateMaterial: (
		id: string,
		updates: UpdateMaterialPayload
	) => Promise<LearningMaterial>
	deleteMaterial: (id: string) => Promise<void>
	publishMaterial: (id: string) => Promise<LearningMaterial>
	unpublishMaterial: (id: string) => Promise<LearningMaterial>
	addResource: (
		materialId: string,
		resource: CreateResourcePayload
	) => Promise<void>
	deleteResource: (
		materialId: string,
		resourceId: string
	) => Promise<void>
}

export const learningStore = create<LearningState>()((set) => ({
	materials: [],
	total: 0,
	selectedMaterial: null,
	categoryStats: [],
	relayTypeStats: [],
	userHistory: [],
	userPins: [],
	relatedMaterials: [],
	loading: false,

	fetchMaterials: async (params) => {
		set({ loading: true })
		const res = await fetchMaterials(params)
		set({
			materials: res.materials,
			total: res.total,
			loading: false
		})
	},

	fetchMaterialsAdmin: async (params) => {
		set({ loading: true })
		const res = await fetchMaterialsAdmin(params)
		set({
			materials: res.materials,
			total: res.total,
			loading: false
		})
	},

	fetchMaterialById: async (id) => {
		set({ loading: true })
		const material = await fetchMaterialById(id)
		set({ selectedMaterial: material, loading: false })
		return material
	},

	fetchMaterialsByRelayType: async (relayTypeId, page, limit) => {
		set({ loading: true })
		const res = await fetchMaterialsByRelayType(
			relayTypeId,
			page,
			limit
		)
		set({
			materials: res.materials,
			total: res.total,
			loading: false
		})
	},

	fetchCategoryStats: async () => {
		const stats = await fetchCategoryStats()
		set({ categoryStats: stats })
	},

	fetchRelayTypeStats: async () => {
		const stats = await fetchRelayTypeStats()
		set({ relayTypeStats: stats })
	},

	logView: async (materialId) => {
		const res = await logMaterialView(materialId)
		set({ relatedMaterials: res.relatedMaterials })
		return res
	},

	togglePin: async (materialId, isPinned) => {
		if (isPinned) {
			await unpinMaterial(materialId)
			set((state) => ({
				userPins: state.userPins.filter(
					(m) => m.id !== materialId
				)
			}))
		} else {
			await pinMaterial(materialId)
		}
	},

	fetchUserHistory: async (limit) => {
		const history = await fetchUserHistory(limit)
		set({ userHistory: history })
	},

	fetchUserPins: async () => {
		const pins = await fetchUserPins()
		set({ userPins: pins })
	},

	createMaterial: async (payload) => {
		const material = await createMaterialApi(payload)
		set((state) => ({
			materials: [material, ...state.materials],
			total: state.total + 1
		}))
		return material
	},

	updateMaterial: async (id, updates) => {
		const material = await updateMaterialApi(id, updates)
		set((state) => ({
			materials: state.materials.map((m) =>
				m.id === id ? material : m
			),
			selectedMaterial:
				state.selectedMaterial?.id === id
					? material
					: state.selectedMaterial
		}))
		return material
	},

	deleteMaterial: async (id) => {
		await deleteMaterialApi(id)
		set((state) => ({
			materials: state.materials.filter((m) => m.id !== id),
			total: state.total - 1,
			selectedMaterial:
				state.selectedMaterial?.id === id
					? null
					: state.selectedMaterial
		}))
	},

	publishMaterial: async (id) => {
		const material = await publishMaterialApi(id)
		set((state) => ({
			materials: state.materials.map((m) =>
				m.id === id ? material : m
			)
		}))
		return material
	},

	unpublishMaterial: async (id) => {
		const material = await unpublishMaterialApi(id)
		set((state) => ({
			materials: state.materials.map((m) =>
				m.id === id ? material : m
			)
		}))
		return material
	},

	addResource: async (materialId, resource) => {
		const newResource = await addResourceApi(
			materialId,
			resource
		)
		set((state) => {
			if (state.selectedMaterial?.id === materialId) {
				return {
					selectedMaterial: {
						...state.selectedMaterial,
						resources: [
							...(state.selectedMaterial.resources ||
								[]),
							newResource
						]
					}
				}
			}
			return {}
		})
	},

	deleteResource: async (materialId, resourceId) => {
		await deleteResourceApi(materialId, resourceId)
		set((state) => {
			if (state.selectedMaterial?.id === materialId) {
				return {
					selectedMaterial: {
						...state.selectedMaterial,
						resources:
							state.selectedMaterial.resources?.filter(
								(r) => r.id !== resourceId
							)
					}
				}
			}
			return {}
		})
	}
}))
